import classNames from "classnames";

import type { RugbyMatchControl } from "src/types/matchControl";
import type { Clock } from "src/types/matchControl/clock";
import type { RugbyContestant } from "src/types/matchControl/contestant";
import type { Control } from "src/types/matchControl/control";

import ClockRow from "../../components/ClockRow";
import Period from "../../components/Period";
import styles from "../../MatchControl.module.scss";
import type { MatchControlVariant } from "../../types";

import RugbyScoringEvents from "./RugbyScoringEvents";
import ScoreRow from "./ScoreRow";

type Props = {
	matchControl: RugbyMatchControl;
	matchControlGraphics: Control[];
	handleDispatchControl: (control?: Control) => void;
	handleStartTime: (clock: Clock) => void;
	handleStopTime: (clock: Clock) => void;
	handleChangeTime: (clock: Clock, time: number) => void;
	handleChangeShortName: (contestant: RugbyContestant, name: string) => void;
	handleChangeColor: (
		contestant: RugbyContestant,
		color: "primaryColor" | "secondaryColor",
		value: string,
	) => void;
	variant?: MatchControlVariant;
	handleUpdateMatchControl: (matchControl: RugbyMatchControl) => void;
	handleAddScoringEvent: (contestant: RugbyContestant, eventType: string) => void;
	handleRemoveScoringEvent: (contestant: RugbyContestant, eventType: string) => void;
};

const Rugby = ({
	matchControl,
	matchControlGraphics,
	handleChangeColor,
	handleChangeShortName,
	handleChangeTime,
	handleDispatchControl,
	handleStartTime,
	handleStopTime,
	handleUpdateMatchControl,
	handleAddScoringEvent,
	handleRemoveScoringEvent,
	variant = "default",
}: Props) => {
	const { clocks, contestants } = matchControl;
	const [mainClock] = clocks;
	const [home, away] = contestants;

	return (
		<div
			className={classNames(styles.MainWrapper, variant === "default" && styles.MainWrapperCompact)}
		>
			<div className={styles.ItemWrapper}>
				<ClockRow
					mainClock={mainClock}
					matchControlGraphics={matchControlGraphics}
					isCompact={variant === "default"}
					handleStartTime={handleStartTime}
					handleStopTime={handleStopTime}
					handleDispatchControl={handleDispatchControl}
					handleChangeTime={handleChangeTime}
				/>
			</div>

			<div className={styles.Divider} />

			<div className={styles.ItemWrapper}>
				<ScoreRow
					home={home}
					away={away}
					isCompact={variant === "default"}
					handleChangeColor={handleChangeColor}
					handleChangeShortName={handleChangeShortName}
				/>
			</div>

			<div className={styles.Divider} />

			<div className="flex items-center gap-2">
				<div className="flex-auto min-w-0 font-semibold">Half</div>
				<div>
					<Period
						period={matchControl.period}
						isCompact={variant === "default"}
						handleAddPeriod={() =>
							handleUpdateMatchControl({
								...matchControl,
								period: Math.min(2, matchControl.period + 1),
							})
						}
						handleRemovePeriod={() =>
							handleUpdateMatchControl({
								...matchControl,
								period: Math.max(1, matchControl.period - 1),
							})
						}
					/>
				</div>
			</div>

			<div className={styles.Divider} />

			<div className={styles.ItemWrapper}>
				<div className="flex flex-row gap-6 w-full">
					<div className="flex flex-col gap-4 flex-1 min-w-0">
						<div className="text-sm font-semibold text-center">{home.shortName}</div>
						<RugbyScoringEvents
							contestant={home}
							isCompact={variant === "default"}
							onAddTry={() => handleAddScoringEvent(home, "try")}
							onRemoveTry={() => handleRemoveScoringEvent(home, "try")}
							onAddConversion={() => handleAddScoringEvent(home, "conversion")}
							onRemoveConversion={() => handleRemoveScoringEvent(home, "conversion")}
							onAddPenaltyKick={() => handleAddScoringEvent(home, "penalty_kick")}
							onRemovePenaltyKick={() => handleRemoveScoringEvent(home, "penalty_kick")}
							onAddDropGoal={() => handleAddScoringEvent(home, "drop_goal")}
							onRemoveDropGoal={() => handleRemoveScoringEvent(home, "drop_goal")}
						/>
					</div>

					<div className="border-r border-elevated-2" />

					<div className="flex flex-col gap-4 flex-1 min-w-0">
						<div className="text-sm font-semibold text-center">{away.shortName}</div>
						<RugbyScoringEvents
							contestant={away}
							isCompact={variant === "default"}
							onAddTry={() => handleAddScoringEvent(away, "try")}
							onRemoveTry={() => handleRemoveScoringEvent(away, "try")}
							onAddConversion={() => handleAddScoringEvent(away, "conversion")}
							onRemoveConversion={() => handleRemoveScoringEvent(away, "conversion")}
							onAddPenaltyKick={() => handleAddScoringEvent(away, "penalty_kick")}
							onRemovePenaltyKick={() => handleRemoveScoringEvent(away, "penalty_kick")}
							onAddDropGoal={() => handleAddScoringEvent(away, "drop_goal")}
							onRemoveDropGoal={() => handleRemoveScoringEvent(away, "drop_goal")}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Rugby;
